import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/platform/AppShell";
import { PageTitle } from "@/components/platform/Badges";
import { RetentionDashboard } from "@/components/retention/RetentionDashboard";
import { RuleLibrary } from "@/components/retention/RuleLibrary";
import { RuleDialog } from "@/components/retention/RuleDialog";
import { CertificatesList, DispositionQueue, HoldDialog, HoldsList } from "@/components/retention/RetentionLists";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { useRetentionMeta } from "@/lib/retention";

export default function RetentionManager() {
  const auth = useAuth();
  const meta = useRetentionMeta();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "dashboard";
  const [d, setD] = useState({ dash: null, rules: [], batches: [], holds: [], certs: [] });
  const [dlg, setDlg] = useState(null);
  const canEdit = auth.can("retention", "edit");

  const load = useCallback(async () => {
    const [a, b, c, e, f] = await Promise.all(["/retention/dashboard", "/retention/rules", "/retention/batches", "/retention/holds",
      "/retention/certificates"].map((u) => api.get(u)));
    setD({ dash: a.data, rules: b.data.rules, batches: c.data.batches, holds: e.data.holds, certs: f.data.certificates });
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const generate = async () => {
    try {
      const { data } = await api.post("/retention/generate");
      toast.success(data.created.length ? `Created ${data.created.join(", ")} for review` : "No new batches due. Every active rule already has an open batch or one for this month.");
      load();
    } catch (e) { toast.error(apiError(e, "Could not generate")); }
  };

  return (
    <AppShell testId="retention-page">
      <PageTitle eyebrow="Operations workspace" title="Retention Manager"
                 subtitle="Retention rules linked to RoPA, monthly disposition batches for human review and approval, holds and deletion certificates. Personal data is never stored."
                 actions={canEdit && <>
                   <Button variant="outline" onClick={generate} data-testid="retention-generate-button"><RefreshCw className="w-4 h-4 mr-1" /> Generate due batches</Button>
                   <Button variant="outline" onClick={() => setDlg("hold")} data-testid="retention-new-hold"><Plus className="w-4 h-4 mr-1" /> New hold</Button>
                   <Button onClick={() => setDlg("rule")} className="bg-[#008080] hover:bg-[#008080]/90" data-testid="retention-new-rule"><Plus className="w-4 h-4 mr-1" /> New rule</Button>
                 </>} />
      <Tabs value={view} onValueChange={(v) => setParams({ view: v })}>
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto" data-testid="retention-tabs">
          <TabsTrigger value="dashboard" data-testid="rtab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="rules" data-testid="rtab-rules">Rule library ({d.rules.length})</TabsTrigger>
          <TabsTrigger value="queue" data-testid="rtab-queue">Disposition queue ({d.batches.length})</TabsTrigger>
          <TabsTrigger value="holds" data-testid="rtab-holds">Legal holds ({d.holds.length})</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="rtab-certificates">Deletion certificates ({d.certs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-5"><RetentionDashboard d={d.dash} meta={meta} /></TabsContent>
        <TabsContent value="rules" className="mt-5"><RuleLibrary rules={d.rules} meta={meta} canEdit={canEdit} onChanged={load} /></TabsContent>
        <TabsContent value="queue" className="mt-5"><DispositionQueue batches={d.batches} /></TabsContent>
        <TabsContent value="holds" className="mt-5"><HoldsList holds={d.holds} /></TabsContent>
        <TabsContent value="certificates" className="mt-5"><CertificatesList certs={d.certs} /></TabsContent>
      </Tabs>
      <RuleDialog open={dlg === "rule"} onOpenChange={(o) => !o && setDlg(null)} meta={meta} rule={null} onSaved={load} />
      <HoldDialog open={dlg === "hold"} onOpenChange={(o) => !o && setDlg(null)} meta={meta} onSaved={load} />
    </AppShell>
  );
}
